import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  faBan,
  faPencil,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FormField } from "../../../../interfaces/form-field.model";
import { UserManagementService } from "src/app/services/administration/user-management.service";
import { NotifierService } from "angular-notifier";
import { of, switchMap, tap } from "rxjs";
import { map } from "rxjs/operators";
import { isEmptyGuid } from "../../../../services/util.service";

@Component({
  selector: "app-user-management-detail",
  templateUrl: "./user-management-detail.component.html",
  styleUrls: ["./user-management-detail.component.css"],
})
export class UserManagementDetailComponent implements OnInit {
  // ICONS
  faPencil = faPencil;
  faBan = faBan;
  faPlus = faPlus;

  mode = "Add";
  userId: string = "";
  activeRoles: any[] = [];
  activeTeams: any[] = [];
  user!: FormField[];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userManagementService: UserManagementService,
    private notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.setModeAndUserId();
    this.fetchActiveRolesAndTeams().subscribe({
      next: () => this.setUpFormFields(),
      error: (error) =>
        this.handleError("Error initializing data", error),
    });
  }

  updateFields(event: any): void {
    this.user = event.fields;
  }

  updateUser(): void {
    if (!this.validateForm()) {
      return;
    }

    const updatedUser = this.populateUserRequestBody();
    this.userManagementService.updateUser(updatedUser).subscribe({
      next: (response) =>
        this.handleApiResponse(response, "User updated", "../../user-management"),
      error: (error) =>
        this.handleError("Error updating user", error),
    });
  }

  addUser(): void {
    if (!this.validateForm()) {
      return;
    }

    const newUser = this.populateUserRequestBody();
    this.userManagementService.addUser(newUser).subscribe({
      next: (response) =>
        this.handleApiResponse(response, "User added", "../../user-management"),
      error: (error) => this.handleError("Error adding user", error),
    });
  }

  isFormValid(): boolean {
    if (!this.user || !this.requiredFieldsHaveValues()) {
      return false;
    }

    if (this.mode === "Add") {
      return this.getFieldValue("password") === this.getFieldValue("confirmPassword");
    }

    return true;
  }

  private setModeAndUserId(): void {
    this.activatedRoute.params
      .pipe(
        tap((data) => {
          this.userId = data["id"];
          if (!isEmptyGuid(this.userId)) {
            this.mode = "Detail";
          }
        }),
      )
      .subscribe();
  }

  private fetchActiveRolesAndTeams() {
    return of(null).pipe(
      switchMap(() =>
        this.userManagementService.getAllActiveRoles().pipe(
          tap((response) => this.processActiveRoles(response.body)),
          switchMap(() =>
            this.userManagementService.getAllSalesTeam(),
          ),
          tap((response) => this.processActiveTeams(response.body)),
        ),
      ),
    );
  }

  private processActiveRoles(responseBody: any[]): void {
    this.activeRoles = responseBody
      .filter((role) => role.Status)
      .map((role) => ({
        display: role.Name,
        value: role.Name,
        id: role.Value,
      }));
  }

  private processActiveTeams(responseBody: any[]): void {
    this.activeTeams = responseBody.map((team) => ({
      display: team.TeamName,
      value: team.TeamName,
    }));
  }

  private setUpFormFields(): void {
    if (this.mode === "Add") {
      this.user = this.getAddUserFormFields();
    } else if (this.mode === "Detail") {
      this.userManagementService
        .getUserDetail(this.userId)
        .subscribe({
          next: (response) =>
            (this.user = this.getDetailUserFormFields(response.body)),
          error: (error) =>
            this.handleError("Error getting user data", error),
        });
    }
  }

  private getAddUserFormFields(): FormField[] {
    return [
      {
        type: "text",
        id: "userId",
        label: "User Id",
        required: true,
      },
      {
        type: "doubleText",
        id: "fullName",
        label: "Full Name",
        required: true,
        value: ["", ""],
      },
      {
        type: "password",
        id: "password",
        label: "Password",
        required: true,
      },
      {
        type: "password",
        id: "confirmPassword",
        label: "Confirm password",
        required: true,
      },
      {
        type: "select",
        id: "userType",
        label: "User Type",
        required: true,
        options: this.activeRoles,
      },
      {
        type: "select",
        id: "team",
        label: "Team",
        options: this.activeTeams,
      },
      {
        type: "select",
        id: "rightsLevel",
        label: "Rights Level",
        required: true,
        options: this.getRightsLevelOptions(),
      },
      { type: "text", id: "agent", label: "Agent" },
      { type: "text", id: "dealersStafff", label: "Dealers Stafff" },
      { type: "text", id: "address", label: "Address" },
      {
        type: "text",
        id: "telephoneNumber",
        label: "Telephone Number",
      },
      { type: "text", id: "mobileNumber", label: "Mobile Number" },
      {
        type: "email",
        id: "emailAddress",
        label: "Email Address",
        required: true,
      },
      { type: "text", id: "faxNumber", label: "Fax Number" },
      { type: "text", id: "serialNumber", label: "Serial Number" },
      {
        type: "select",
        id: "status",
        label: "Status",
        required: true,
        value: false,
        options: this.getStatusOptions(),
      },
      { type: "text", id: "HMGICSID", label: "HMGICS ID" },
    ];
  }

  private getDetailUserFormFields(responseBody: any): FormField[] {
    return [
      {
        type: "text",
        id: "userId",
        label: "User Id",
        lock: true,
        required: true,
        value: responseBody.LoginId,
      },
      {
        type: "doubleText",
        id: "fullName",
        label: "Full Name",
        required: true,
        value: [responseBody.FirstName, responseBody.LastName],
      },
      {
        type: "select",
        id: "userType",
        label: "User Type",
        required: true,
        options: this.activeRoles,
        value: responseBody.RoleName,
      },
      {
        type: "select",
        id: "team",
        label: "Team",
        options: this.activeTeams,
        value: responseBody.Team,
      },
      {
        type: "select",
        id: "rightsLevel",
        label: "Rights Level",
        required: true,
        value: responseBody.RightsLevel,
        options: this.getRightsLevelOptions(),
      },
      {
        type: "text",
        id: "agent",
        label: "Agent",
        value: responseBody.Agent,
      },
      {
        type: "text",
        id: "dealersStafff",
        label: "Dealers Stafff",
        value: responseBody.DealersStaff,
      },
      {
        type: "text",
        id: "address",
        label: "Address",
        value: responseBody.Address,
      },
      {
        type: "text",
        id: "telephoneNumber",
        label: "Telephone Number",
        value: responseBody.TelephoneNumber,
      },
      {
        type: "text",
        id: "mobileNumber",
        label: "Mobile Number",
        value: responseBody.MobileNumber,
      },
      {
        type: "email",
        id: "emailAddress",
        label: "Email Address",
        value: responseBody.EmailId,
        required: true,
      },
      {
        type: "text",
        id: "faxNumber",
        label: "Fax Number",
        value: responseBody.FaxNumber,
      },
      {
        type: "text",
        id: "serialNumber",
        label: "Serial Number",
        value: responseBody.SerialNumber,
      },
      {
        type: "select",
        id: "status",
        label: "Status",
        required: true,
        value: responseBody.Status,
        options: this.getStatusOptions(),
      },
      {
        type: "text",
        id: "HMGICSID",
        label: "HMGICS ID",
        value: responseBody.HmgicsId,
      },
    ];
  }

  private populateUserRequestBody(): any {
    return {
      UserId: this.userId,
      LoginId: this.getFieldValue("userId"),
      RoleId: this.getSelectedRoleId(),
      RoleName: this.getFieldValue("userType"),
      FirstName: this.getFieldValue("fullName", 0),
      LastName: this.getFieldValue("fullName", 1),
      Team: this.getFieldValue("team"),
      RightsLevel: this.getFieldValue("rightsLevel"),
      Agent: this.getFieldValue("agent"),
      DealersStaff: this.getFieldValue("dealersStafff"),
      Address: this.getFieldValue("address"),
      EmailId: this.getFieldValue("emailAddress"),
      TelephoneNumber: this.getFieldValue("telephoneNumber"),
      MobileNumber: this.getFieldValue("mobileNumber"),
      FaxNumber: this.getFieldValue("faxNumber"),
      SerialNumber: this.getFieldValue("serialNumber"),
      Status: this.getFieldValue("status"),
      HmgicsId: this.getFieldValue("HMGICSID"),
      Password: this.getFieldValue("password"),
      ConfirmPassword: this.getFieldValue("confirmPassword"),
    };
  }

  private getFieldValue(id: string, index?: number): any {
    const field = this.user.find((f) => f.id === id);
    return index !== undefined ? field?.value[index] : field?.value;
  }

  private getSelectedRoleId(): string | undefined {
    const selectedRole = this.activeRoles.find(
      (role) => role.display === this.getFieldValue("userType"),
    );
    return selectedRole?.id;
  }

  private requiredFieldsHaveValues(): boolean {
    if (!this.user) {
      return false;
    }

    return this.user
      .filter((field) => field.required)
      .every((field) => {
        if (field.id === "rightsLevel" && Number(field.value) === 0) {
          return false;
        }

        if (field.type === "doubleText") {
          return Array.isArray(field.value) &&
            field.value.every((value) => this.hasValue(value));
        }

        return this.hasValue(field.value);
      });
  }

  private hasValue(value: any): boolean {
    return value !== undefined && value !== null &&
      (typeof value !== "string" || value.trim() !== "");
  }

  private validateForm(): boolean {
    if (!this.requiredFieldsHaveValues()) {
      this.notifier.notify("warning", "Please complete all required fields");
      return false;
    }

    if (
      this.mode === "Add" &&
      this.getFieldValue("password") !== this.getFieldValue("confirmPassword")
    ) {
      this.notifier.notify("warning", "Password and Confirm password must match");
      return false;
    }

    return true;
  }

  private getStatusOptions(): any[] {
    return [
      { display: "Disabled", value: false },
      { display: "Enabled", value: true },
    ];
  }

  private getRightsLevelOptions(): any[] {
    return [
      { display: "", value: 0 },
      { display: "1", value: 1 },
      { display: "2", value: 2 },
      { display: "3", value: 3 },
      { display: "4", value: 4 },
    ];
  }

  private handleSuccess(message: string, redirectPath: string): void {
    this.notifier.notify("success", message);
    this.router.navigate([redirectPath], {
      relativeTo: this.activatedRoute,
    });
  }

  private handleApiResponse(
    response: any,
    fallbackSuccessMessage: string,
    redirectPath: string,
  ): void {
    if (response.code !== 0 || response.statusCode < 200 || response.statusCode >= 300) {
      this.notifier.notify("error", response.message || "The operation was not completed");
      return;
    }

    const message = response.message && response.message !== "OK"
      ? response.message
      : fallbackSuccessMessage;
    this.handleSuccess(message, redirectPath);
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    const displayMessage =
      error?.message && error.message !== message
        ? `${message}: ${error.message}`
        : message;
    this.notifier.notify("error", displayMessage);
  }
}
